import { Injectable } from '@angular/core';
import { FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { catchError, forkJoin, Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { Field, FormValues, SelectedUnits } from '../models';
import { NotificationService, NotificationType } from './notification.service';
import { UnitConversionService } from './unit-conversion.service';

// Add the new type definition
export type MinMaxValues = { [key: string]: { min: number; max: number } };

@Injectable({
  providedIn: 'root',
})
export class DynamicFormBaseService {
  constructor(
    private unitConversionService: UnitConversionService,
    private notificationService: NotificationService,
  ) {}

  public buildValidators(field: Field): ValidatorFn[] {
    const validators: ValidatorFn[] = [];
    if (field?.required) {
      validators.push(Validators.required);
    }
    if (field?.min !== undefined && field?.min !== null) {
      validators.push(Validators.min(field.min));
    }
    if (field?.max !== undefined && field?.max !== null) {
      validators.push(Validators.max(field.max));
    }
    return validators;
  }

  initializeUnitIfNeeded(field: Field, selectedUnits: SelectedUnits): void {
    if (!selectedUnits[field.name] && field?.baseUnit) {
      selectedUnits[field.name] = field?.baseUnit;
    }
  }

  storeOriginalMinMax(field: Field, originalMinMax: MinMaxValues): void {
    if (!originalMinMax[field.name] && (field.min !== undefined || field.max !== undefined)) {
      originalMinMax[field.name] = {
        min: field.min,
        max: field.max,
      };
    }
  }

  updateValidators(fieldName: string, min: number, max: number, form: FormGroup): void {
    const control = form.get(fieldName);
    if (control) {
      const validators: ValidatorFn[] = [];
      if (min !== undefined && min !== null) {
        validators.push(Validators.min(min));
      }
      if (max !== undefined && max !== null) {
        validators.push(Validators.max(max));
      }
      control.setValidators(validators);
      control.updateValueAndValidity();
    }
  }

  async convertFormValuesToBaseUnits(formValues: FormValues, formFields: Field[], selectedUnits: SelectedUnits): Promise<FormValues> {
    const convertedValues = { ...formValues };
    const conversionPromises = [];

    for (const field of formFields) {
      const fieldName = field?.name;
      const baseUnit = field?.baseUnit;
      const currentUnit = selectedUnits[fieldName];
      const currentValue = formValues[fieldName];

      if (currentUnit !== baseUnit && currentValue !== undefined && currentValue !== null) {
        const conversionPromise = this.unitConversionService
          .convert(currentValue, currentUnit, baseUnit, field.category)
          .toPromise()
          .then((valueInBaseUnit) => {
            convertedValues[fieldName] = valueInBaseUnit;
          })
          .catch(() => {
            // Keep original value if conversion fails
            convertedValues[fieldName] = currentValue;
          });
        conversionPromises.push(conversionPromise);
      }
    }

    await Promise.all(conversionPromises);
    return convertedValues;
  }

  //eslint-disable-next-line max-params-no-constructor/max-params-no-constructor
  convertMinMax(
    field: Field,
    baseUnit: string,
    newUnit: string,
    valueType: string,
    originalMinMax: MinMaxValues,
    convertedMinMax: MinMaxValues,
    form: FormGroup,
  ): void {
    const originalMin = originalMinMax[field.name]?.min;
    const originalMax = originalMinMax[field.name]?.max;

    // Skip conversion if values are undefined or null
    if ((originalMin === undefined && originalMax === undefined) || !baseUnit || !newUnit || !valueType) {
      return;
    }

    // Convert min value if it exists
    const minConversion =
      originalMin !== undefined && originalMin !== null
        ? this.unitConversionService.convert(originalMin, baseUnit, newUnit, valueType).pipe(catchError(() => of(originalMin)))
        : of(undefined);

    // Convert max value if it exists
    const maxConversion =
      originalMax !== undefined && originalMax !== null
        ? this.unitConversionService.convert(originalMax, baseUnit, newUnit, valueType).pipe(catchError(() => of(originalMax)))
        : of(undefined);

    forkJoin({
      convertedMin: minConversion,
      convertedMax: maxConversion,
    }).subscribe(({ convertedMin, convertedMax }) => {
      convertedMinMax[field.name] = {
        min: convertedMin,
        max: convertedMax,
      };
      this.updateValidators(field.name, convertedMin, convertedMax, form);
    });
  }

  checkTemperatures(formValues: FormValues, selectedUnits: SelectedUnits, formFields: Field[]): Observable<boolean> {
    const inlet = formValues['inletTemperature'];
    const ambient = formValues['ambientTemperature'];

    if (inlet === undefined || inlet === null || ambient === undefined || ambient === null) {
      return of(false);
    }

    const inletUnit = selectedUnits['inletTemperature'];
    const ambientUnit = selectedUnits['ambientTemperature'];

    if (inletUnit === ambientUnit) {
      // If units are the same, direct comparison is fine
      return of(ambient > inlet);
    } else {
      // Convert both to the base unit for comparison
      const inletField = formFields.find((field) => field.name === 'inletTemperature');
      const ambientField = formFields.find((field) => field.name === 'ambientTemperature');

      if (!inletField || !ambientField) {
        return of(false);
      }

      const valueType = inletField.category;
      const inletBaseUnit = inletField.baseUnit;
      const ambientBaseUnit = ambientField.baseUnit;

      // Use forkJoin to wait for both conversions
      return forkJoin({
        inletConverted: this.unitConversionService.convert(inlet, inletUnit, inletBaseUnit, valueType),
        ambientConverted: this.unitConversionService.convert(ambient, ambientUnit, ambientBaseUnit, valueType),
      }).pipe(
        catchError(() => of({ inletConverted: inlet, ambientConverted: ambient })),
        map(({ inletConverted, ambientConverted }) => ambientConverted > inletConverted),
      );
    }
  }

  //eslint-disable-next-line max-params-no-constructor/max-params-no-constructor
  onUnitChange(
    event: Event,
    field: Field,
    form: FormGroup,
    selectedUnits: SelectedUnits,
    originalMinMax: MinMaxValues,
    convertedMinMax: MinMaxValues,
  ): void {
    const newUnit = event?.target?.['value'];
    const valueType = field?.category;
    const baseUnit = field?.baseUnit;

    if (!newUnit || !valueType || !baseUnit) {
      return;
    }

    // Record the unit selection unconditionally, even if there's no value yet to convert.
    // Otherwise a unit picked before a value is typed never gets remembered, and the value
    // typed afterwards silently gets treated as if it were already in the base unit.
    const previousUnit = selectedUnits[field.name];
    selectedUnits[field.name] = newUnit;

    if (field.name === 'airFlow') {
      this.unitConversionService.setSelectedUnit(newUnit);
    }

    const currentValue = Number.parseFloat(form.get(field.name)?.value);
    if (Number.isNaN(currentValue)) {
      // Nothing entered yet — the unit selection above is all that needs to happen.
      return;
    }

    if (previousUnit === baseUnit) {
      // Direct conversion from base unit to new unit
      this.unitConversionService
        .convert(currentValue, baseUnit, newUnit, valueType)
        .pipe(
          catchError((error) => {
            this.notificationService.showNotification(NotificationType.Error, 'An error occurred during unit conversion.');
            return of(currentValue);
          }),
        )
        .subscribe((convertedValue) => {
          form.get(field.name)?.setValue(Number.parseFloat(convertedValue.toString()));
          this.convertMinMax(field, baseUnit, newUnit, valueType, originalMinMax, convertedMinMax, form);
        });
    } else {
      // Convert from current unit to base unit first, then to new unit
      this.unitConversionService
        .convert(currentValue, previousUnit, baseUnit, valueType)
        .pipe(
          catchError(() => of(currentValue)),
          switchMap((valueInBaseUnit) =>
            this.unitConversionService.convert(valueInBaseUnit, baseUnit, newUnit, valueType).pipe(catchError(() => of(valueInBaseUnit))),
          ),
        )
        .subscribe((convertedValue) => {
          form.get(field.name)?.setValue(Number.parseFloat(convertedValue.toString()));
          this.convertMinMax(field, baseUnit, newUnit, valueType, originalMinMax, convertedMinMax, form);
        });
    }
  }
}
